import { useCallback, useEffect, useState } from "react";
import { adminBills, adminCreateBill, adminUpdateBill, adminDeleteBill, getPriorities } from "@/lib/api";
import { positionBadge, StageStepper, STAGES } from "@/lib/bills";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import ResourceList from "@/components/admin/ResourceList";
import ResourceDrawer from "@/components/admin/ResourceDrawer";

const POSITIONS = ["Support", "Oppose", "Monitor", "Amend"];
const EMPTY = {
  number: "", session: "2026 Regular Session", title: "", position: "Monitor", stage: "Introduced",
  issue_areas: [], committee: "", hearing_date: "", why_it_matters: "", summary: "",
};

function BillRow({ b, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-stone-200 p-5 flex items-start justify-between gap-4" data-testid={`admin-bill-${b.number}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${positionBadge(b.position)}`}>{b.position}</span>
          <span className="font-mono text-xs text-stone-500">{b.number}</span>
        </div>
        <div className="font-serif text-xl text-ccc-plum">{b.title}</div>
        <div className="mt-2"><StageStepper stage={b.stage}/></div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={() => onEdit(b)} className="text-ccc-purple text-sm underline underline-offset-2">Edit</button>
        <button onClick={() => onDelete(b.id)} className="text-red-600 text-sm" aria-label="Delete"><Trash2 size={14}/></button>
      </div>
    </div>
  );
}

function BillForm({ editing, setEditing, priorities }) {
  const toggleIssue = (slug, checked) => setEditing({
    ...editing,
    issue_areas: checked ? [...editing.issue_areas, slug] : editing.issue_areas.filter(x => x !== slug),
  });
  return (
    <div className="grid grid-cols-2 gap-3">
      <input placeholder="Number" value={editing.number} onChange={e => setEditing({...editing, number: e.target.value})} className="px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple" data-testid="bill-number"/>
      <input placeholder="Session" value={editing.session} onChange={e => setEditing({...editing, session: e.target.value})} className="px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple"/>
      <input placeholder="Title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple" data-testid="bill-title"/>
      <select value={editing.position} onChange={e => setEditing({...editing, position: e.target.value})} className="px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple">
        {POSITIONS.map(p => <option key={p}>{p}</option>)}
      </select>
      <select value={editing.stage} onChange={e => setEditing({...editing, stage: e.target.value})} className="px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple">
        {STAGES.map(s => <option key={s}>{s}</option>)}
      </select>
      <input placeholder="Committee" value={editing.committee || ""} onChange={e => setEditing({...editing, committee: e.target.value})} className="px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple"/>
      <input placeholder="Hearing date" type="date" value={editing.hearing_date || ""} onChange={e => setEditing({...editing, hearing_date: e.target.value})} className="px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple"/>
      <div className="col-span-2">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Issue Areas</div>
        <div className="flex flex-wrap gap-2">
          {priorities.map(p => (
            <label key={p.slug} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-stone-300">
              <input type="checkbox" checked={editing.issue_areas.includes(p.slug)} onChange={e => toggleIssue(p.slug, e.target.checked)}/>
              {p.title}
            </label>
          ))}
        </div>
      </div>
      <textarea placeholder="Why it matters" rows={3} value={editing.why_it_matters} onChange={e => setEditing({...editing, why_it_matters: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple"/>
      <textarea placeholder="Summary" rows={3} value={editing.summary} onChange={e => setEditing({...editing, summary: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300 focus:outline-none focus:border-ccc-purple"/>
    </div>
  );
}

export default function Bills() {
  const [items, setItems] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => adminBills().then(setItems), []);
  useEffect(() => { load(); getPriorities().then(setPriorities); }, [load]);

  const save = async () => {
    try {
      if (items.find(i => i.id === editing.id)) {
        await adminUpdateBill(editing.id, editing); toast.success("Bill updated");
      } else {
        await adminCreateBill(editing); toast.success("Bill created");
      }
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this bill?")) return;
    await adminDeleteBill(id); load();
  };

  return (
    <ResourceList
      eyebrow="Legislative"
      title="Bills."
      testId="admin-bills"
      newLabel="New Bill"
      newButtonTestId="new-bill"
      onNew={() => setEditing({ ...EMPTY })}
    >
      <div className="grid gap-3">
        {items.map(b => <BillRow key={b.id} b={b} onEdit={setEditing} onDelete={remove}/>)}
      </div>

      <ResourceDrawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Bill" : "New Bill"}
        testId="bill-drawer"
        size="lg"
        onSave={save}
        saveLabel="Save Bill"
        saveTestId="save-bill"
      >
        {editing && <BillForm editing={editing} setEditing={setEditing} priorities={priorities}/>}
      </ResourceDrawer>
    </ResourceList>
  );
}
